/**
 * Core logic for preventing browser navigation gestures and pull-to-refresh
 * on a specific element via CSS overscroll-behavior and touch event handling.
 *
 * Supports the following axes / modes:
 *   "x"                | "horizontal"     — suppress left/right navigation swipe
 *   "y"                | "vertical"       — suppress pull-to-refresh
 *   "both"             | "all"            — suppress all overscroll gestures
 *   "pull-to-refresh"                     — alias for "y"
 *   "navigation-swipe"                    — alias for "x"
 */

export type GestureMode =
  | 'x'
  | 'y'
  | 'horizontal'
  | 'vertical'
  | 'both'
  | 'all'
  | 'pull-to-refresh'
  | 'navigation-swipe';

export interface PreventGesturesInstance {
  destroy(): void;
}

const ALIAS: Record<GestureMode, 'x' | 'y' | 'both'> = {
  x: 'x',
  horizontal: 'x',
  'navigation-swipe': 'x',
  y: 'y',
  vertical: 'y',
  'pull-to-refresh': 'y',
  both: 'both',
  all: 'both',
};

/**
 * Apply overscroll-behavior CSS properties to `el` to suppress the
 * requested gesture axes. Optional touch event suppression can be enabled
 * for browsers that ignore overscroll-behavior on inner elements.
 */
export interface PreventGesturesOptions {
  eventsToo?: boolean;
}

export function applyPreventGestures(
  el: HTMLElement,
  mode: GestureMode = 'both',
  options: PreventGesturesOptions = {},
): PreventGesturesInstance {
  const axis = ALIAS[mode] ?? 'both';
  const eventsToo = options.eventsToo === true;

  // CSS overscroll-behavior (works in Chrome/Firefox/modern Safari)
  const prev = {
    overscrollBehavior: el.style.overscrollBehavior,
    overscrollBehaviorX: el.style.overscrollBehaviorX,
    overscrollBehaviorY: el.style.overscrollBehaviorY,
  };

  if (axis === 'both') {
    el.style.overscrollBehavior = 'contain';
  } else {
    el.style.overscrollBehavior = prev.overscrollBehavior;
  }

  if (axis === 'x') {
    el.style.overscrollBehaviorX = 'contain';
  } else if (axis !== 'both') {
    el.style.overscrollBehaviorX = prev.overscrollBehaviorX;
  }

  if (axis === 'y') {
    el.style.overscrollBehaviorY = 'contain';
  } else if (axis !== 'both') {
    el.style.overscrollBehaviorY = prev.overscrollBehaviorY;
  }

  if (!eventsToo) {
    return {
      destroy() {
        el.style.overscrollBehavior = prev.overscrollBehavior;
        el.style.overscrollBehaviorX = prev.overscrollBehaviorX;
        el.style.overscrollBehaviorY = prev.overscrollBehaviorY;
      },
    };
  }

  // Touchmove guard for older Safari / WebKit
  let startX = 0;
  let startY = 0;

  const onTouchStart = (e: TouchEvent) => {
    if (e.touches.length === 1) {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }
  };

  const onTouchMove = (e: TouchEvent) => {
    if (e.touches.length !== 1) return;
    const dx = Math.abs(e.touches[0].clientX - startX);
    const dy = Math.abs(e.touches[0].clientY - startY);

    const suppressX = axis === 'x' || axis === 'both';
    const suppressY = axis === 'y' || axis === 'both';

    // Suppress if dominant axis matches what we guard
    if (suppressX && dx > dy) {
      // Horizontal swipe — only prevent if element can't scroll more that way
      const el_ = el as HTMLElement;
      const atLeftEdge = el_.scrollLeft <= 0;
      const atRightEdge = el_.scrollLeft >= el_.scrollWidth - el_.clientWidth;
      if (atLeftEdge || atRightEdge) {
        e.preventDefault();
      }
    } else if (suppressY && dy > dx) {
      // Vertical swipe — prevent overscroll (pull-to-refresh)
      const el_ = el as HTMLElement;
      const atTopEdge = el_.scrollTop <= 0;
      const atBottomEdge = el_.scrollTop >= el_.scrollHeight - el_.clientHeight;
      if (atTopEdge || atBottomEdge) {
        e.preventDefault();
      }
    }
  };

  el.addEventListener('touchstart', onTouchStart, { passive: true });
  el.addEventListener('touchmove', onTouchMove, { passive: false });

  return {
    destroy() {
      // Restore original CSS values
      el.style.overscrollBehavior = prev.overscrollBehavior;
      el.style.overscrollBehaviorX = prev.overscrollBehaviorX;
      el.style.overscrollBehaviorY = prev.overscrollBehaviorY;
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
    },
  };
}
